import React, { useContext, useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import EventsList from './EventsList';
import RefereesList from './RefereesList';
import AthletesList from './AthletesList';
import CoachesList from './CoachesList';
import ClubProfile from './ClubProfile';
import './DashboardNav.css';
import { faFilePdf } from '@fortawesome/free-solid-svg-icons';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faRightFromBracket,
  faBars,
  faXmark,
  faCalendarDays,
  faScaleBalanced,
  faPersonRunning,
  faChalkboardUser
} from '@fortawesome/free-solid-svg-icons';


// Helper pt. conversie „truthy” robustă (0/1, true/false, '1'/'0', 'true'/'false')
const toBool = (v) => v === true || v === 1 || v === '1' || v === 'true';

export default function Dashboard() {
  const navigate = useNavigate();
  const { auth, logout } = useContext(AuthContext);

  const [activeTab, setActiveTab] = useState('events');
  const [menuOpen, setMenuOpen] = useState(false);

  const [events, setEvents] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [referees, setReferees] = useState([]);
  const [coaches, setCoaches] = useState([]);

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const location = useLocation();

  const [closing, setClosing] = useState(false);

  const handleCloseMenu = () => {
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      setMenuOpen(false);
    }, 300); // egal cu durata CSS
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    setMenuOpen(false); // schimbă tab instant, fără slide-out
  };
  useEffect(() => {
    if (location.search) {
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);


  // Fetch pentru liste (legăm sportivi/arbitri/antrenori de selectedYear)
  useEffect(() => {
    const token = localStorage.getItem('jwtToken');
    if (!token) {
      setFetchError('Token JWT lipsă. Reautentifică-te.');
      return;
    }

    setLoading(true);
    setFetchError(null);

    const endpoints = {
      events: 'https://www.fro.ro/api/events/index.php',
      athletes: `https://www.fro.ro/api/athletes/index.php?year=${selectedYear}`,
      referees: `https://www.fro.ro/api/referees/index.php?year=${selectedYear}`,
      coaches: `https://www.fro.ro/api/coaches/index.php?year=${selectedYear}`,
    };

    Promise.all(
      Object.entries(endpoints).map(([key, url]) =>
        fetch(url, { headers: { Authorization: 'Bearer ' + token } })
          .then(async (res) => {
            if (!res.ok) {
              const text = await res.text();
              throw new Error(`${key} ${res.status}: ${text}`);
            }
            return res.json();
          })
          .then((data) => ({ key, data }))
      )
    )
      .then((results) => {
        results.forEach(({ key, data }) => {
          if (data?.success) {
            const list = data.data || [];
            if (key === 'events') setEvents(list);
            if (key === 'athletes') setAthletes(list);
            if (key === 'referees') setReferees(list);
            if (key === 'coaches') setCoaches(list);
          } else {
            setFetchError((prev) => prev || data?.message || `Eroare la încărcarea ${key}`);
          }
        });
      })
      .catch((err) => {
        setFetchError(err.message || 'Eroare la încărcarea datelor.');
      })
      .finally(() => setLoading(false));
  }, [selectedYear]);

  // Statistici (memoizate)
  const statsAthletes = useMemo(() => {
    const total = athletes.length;
    const active = athletes.filter((i) => toBool(i.is_legitimated)).length;
    return { total, active, inactive: total - active };
  }, [athletes]);

  const statsReferees = useMemo(() => {
    const total = referees.length;
    const active = referees.filter((i) => toBool(i.is_legitimated)).length;
    return { total, active, inactive: total - active };
  }, [referees]);

  const statsCoaches = useMemo(() => {
    const total = coaches.length;
    const active = coaches.filter((i) => toBool(i.is_legitimated)).length;
    return { total, active, inactive: total - active };
  }, [coaches]);

  // Statistici evenimente
  const now = new Date();
  const totalEvents = events.length;
  const futureEvents = events.filter((ev) => new Date(ev.start_date) > now).length;
  const pastEvents = events.filter((ev) => new Date(ev.end_date) < now).length;

  return (
    <div className="dashboard-gradient-bg">
      {/* HEADER */}
      <div className="dashboard-header">
        <h1 className="dashboard-title">{auth.club?.name}</h1>

        {/* Buton meniu mobil */}
        <button
          className="dashboard-menu-btn"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Deschide/închide meniul"
        >
          <FontAwesomeIcon icon={menuOpen ? faXmark : faBars} size="2x" />
        </button>

        {/* NAV Desktop */}
        <div className="dashboard-user-info desktop-only">
          <nav className="dashboard-nav">
            <button
              className={`dashboard-nav-btn${activeTab === 'events' ? ' active' : ''}`}
              onClick={() => setActiveTab('events')}
            >
              Evenimente
            </button>
            <button
              className={`dashboard-nav-btn${activeTab === 'referees' ? ' active' : ''}`}
              onClick={() => setActiveTab('referees')}
            >
              Arbitri
            </button>
            <button
              className={`dashboard-nav-btn${activeTab === 'athletes' ? ' active' : ''}`}
              onClick={() => setActiveTab('athletes')}
            >
              Sportivi
            </button>
            <button
              className={`dashboard-nav-btn${activeTab === 'coaches' ? ' active' : ''}`}
              onClick={() => setActiveTab('coaches')}
            >
              Antrenori
            </button>
          </nav>

          <div className="dashboard-actions">
            <button
              className="dashboard-profile-btn"
              title="Profil club"
              onClick={() => setActiveTab('clubProfile')}
            >
              <FontAwesomeIcon icon={faUser} size="2x" color="#222" />
            </button>
            <button className="dashboard-logout-btn" title="Logout" onClick={logout}>
              <FontAwesomeIcon icon={faRightFromBracket} size="lg" color="#222" />
              Log-out
            </button>
          </div>
        </div>

      </div>
      {/* === NAV Mobil === */}
      {menuOpen && (
        <div
          className="dashboard-mobile-overlay"
          onClick={() => setMenuOpen(false)} // click pe fundal = închidere instant
        >
          <div
            className={`dashboard-mobile-menu ${closing ? 'closing' : 'open'}`}
            onClick={(e) => e.stopPropagation()}
            onAnimationEnd={() => {
              if (closing) {
                setMenuOpen(false);
                setClosing(false);
              }
            }}
          >
            <button
              className="dashboard-close-btn"
              onClick={() => setClosing(true)}
              aria-label="Închide meniul"
            >
              <FontAwesomeIcon icon={faXmark} size="lg" />
            </button>

            <nav className="dashboard-mobile-nav">
              <button
                className={`dashboard-nav-btn${activeTab === 'events' ? ' active' : ''}`}
                onClick={() => {
                  setActiveTab('events');
                  setMenuOpen(false);
                }}
              >
                <FontAwesomeIcon icon={faCalendarDays} /> Evenimente
              </button>

              <button
                className={`dashboard-nav-btn${activeTab === 'referees' ? ' active' : ''}`}
                onClick={() => {
                  setActiveTab('referees');
                  setMenuOpen(false);
                }}
              >
                <FontAwesomeIcon icon={faScaleBalanced} /> Arbitri
              </button>

              <button
                className={`dashboard-nav-btn${activeTab === 'athletes' ? ' active' : ''}`}
                onClick={() => {
                  setActiveTab('athletes');
                  setMenuOpen(false);
                }}
              >
                <FontAwesomeIcon icon={faPersonRunning} /> Sportivi
              </button>

              <button
                className={`dashboard-nav-btn${activeTab === 'coaches' ? ' active' : ''}`}
                onClick={() => {
                  setActiveTab('coaches');
                  setMenuOpen(false);
                }}
              >
                <FontAwesomeIcon icon={faChalkboardUser} /> Antrenori
              </button>

              <hr className="dashboard-divider" />

              <button
                className={`dashboard-nav-btn${activeTab === 'clubProfile' ? ' active' : ''}`}
                onClick={() => {
                  setActiveTab('clubProfile');
                  setMenuOpen(false);
                }}
              >
                <FontAwesomeIcon icon={faUser} /> Profil
              </button>

              <button className="dashboard-nav-btn logout" onClick={logout}>
                <FontAwesomeIcon icon={faRightFromBracket} /> Logout
              </button>
            </nav>
          </div>

        </div>

      )}



      {/* Mesaj stare globală */}
      {fetchError && (
        <div className="dashboard-alert error" role="alert">
          {fetchError}
        </div>
      )}
      {loading && <div className="dashboard-loading">Se încarcă datele…</div>}

      {/* === Statistici Evenimente === */}
      {activeTab === 'events' && (
        <div className="dashboard-stats-elements">
          <h2 className="dashboard-stat-title">Evenimente club</h2>
          <div className="dashboard-subnav-link">
            <a
              href="https://fro.ro/app/docs/taxe_vize_2026_09.12.2025.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FontAwesomeIcon icon={faFilePdf} className="pdf-icon" />
              Taxe si vize
            </a>
          </div>
          <div className="dashboard-stats-bar">
            <div className="dashboard-stat">
              <span>Total</span>
              <div className="dashboard-stat-pill total">{totalEvents}</div>
            </div>
            <div className="dashboard-stat">
              <span>Viitoare</span>
              <div className="dashboard-stat-pill future">{futureEvents}</div>
            </div>
            <div className="dashboard-stat">
              <span>Anterioare</span>
              <div className="dashboard-stat-pill past">{pastEvents}</div>
            </div>
          </div>
        </div>
      )}

      {/* === Statistici Sportivi — pe anul selectat === */}
      {activeTab === 'athletes' && (
        <div className="dashboard-stats-elements">
          <h2 className="dashboard-stat-title">Sportivi club — {selectedYear}</h2>
          <div className="dashboard-stats-bar">
            <div className="dashboard-stat">
              <span>Total</span>
              <div className="dashboard-stat-pill total">{statsAthletes.total}</div>
            </div>
            <div className="dashboard-stat">
              <span>Activi</span>
              <div className="dashboard-stat-pill future">{statsAthletes.active}</div>
            </div>
            <div className="dashboard-stat">
              <span>Inactivi</span>
              <div className="dashboard-stat-pill past">{statsAthletes.inactive}</div>
            </div>
          </div>
        </div>
      )}

      {/* === Statistici Arbitri — pe anul selectat === */}
      {activeTab === 'referees' && (
        <div className="dashboard-stats-elements">
          <h2 className="dashboard-stat-title">Arbitri club — {selectedYear}</h2>
          <div className="dashboard-stats-bar">
            <div className="dashboard-stat">
              <span>Total</span>
              <div className="dashboard-stat-pill total">{statsReferees.total}</div>
            </div>
            <div className="dashboard-stat">
              <span>Activi</span>
              <div className="dashboard-stat-pill future">{statsReferees.active}</div>
            </div>
            <div className="dashboard-stat">
              <span>Inactivi</span>
              <div className="dashboard-stat-pill past">{statsReferees.inactive}</div>
            </div>
          </div>
        </div>
      )}

      {/* === Statistici Antrenori — pe anul selectat === */}
      {activeTab === 'coaches' && (
        <div className="dashboard-stats-elements">
          <h2 className="dashboard-stat-title">Antrenori club — {selectedYear}</h2>
          <div className="dashboard-stats-bar">
            <div className="dashboard-stat">
              <span>Total</span>
              <div className="dashboard-stat-pill total">{statsCoaches.total}</div>
            </div>
            <div className="dashboard-stat">
              <span>Activi</span>
              <div className="dashboard-stat-pill future">{statsCoaches.active}</div>
            </div>
            <div className="dashboard-stat">
              <span>Inactivi</span>
              <div className="dashboard-stat-pill past">{statsCoaches.inactive}</div>
            </div>
          </div>
        </div>
      )}

      {/* === Liste === */}
      {activeTab === 'events' && <EventsList />}

      {activeTab === 'athletes' && (
        <AthletesList
          selectedYear={selectedYear}
          onYearChange={setSelectedYear}
          onListUpdate={setAthletes}
        />
      )}

      {activeTab === 'referees' && (
        <RefereesList
          selectedYear={selectedYear}
          onYearChange={setSelectedYear}
          onListUpdate={setReferees}
        />
      )}

      {activeTab === 'coaches' && (
        <CoachesList
          selectedYear={selectedYear}
          onYearChange={setSelectedYear}
          onListUpdate={setCoaches}
        />
      )}

      {activeTab === 'clubProfile' && <ClubProfile />}
    </div>
  );
}
